/* eslint-disable react/forbid-prop-types */
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';
import PropTypes from 'prop-types';
import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ACTIVITY_STATE__COMPLETED,
  ACTIVITY_STATE__ACTIVE,
  ACTIVITY_STATE__ABORTED,
  ACTIVITY_STATE__CANCELED,
  ACTIVITY_STATE__EXPIRED,
  ACTIVITY_STATE__SCHEDULED,
  ACTIVITY_STATE__PLANNED,
  ACTIVITY_STATE__OMITTED,
  ACTIVITY_STATE__SUSPENDED,
} from '../../common/constants';
import Button from '../Button';
import CalendarMilestone from './CalendarMilestone';
import {
  getCurrentMonthDays,
  getNextMonthDays,
  getPrevMonthDays,
  getWeekDays,
} from './helpers';
import useCalendarItems from './useCalendarItems';
import CalendarDay from './CalendarDay';

// NOTE: I think there could be some confusion around "currentDate"
// which is actually more of a current month in Date format.
// I may need to refactor this

const Calendar = ({
  activities,
  clinicalEvents,
  className,
  onShowMoreActivities,
  onClinicalEventClick,
}) => {
  const { t, i18n } = useTranslation();

  const { language } = i18n;
  const languageDirection = i18n.dir(language);

  const [currentDate, setCurrentDate] = useState();

  const dayNames = getWeekDays(language);

  useEffect(() => {
    setCurrentDate(new Date());
  }, []);

  const handleOnSelectPrevMonth = () =>
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1),
    );
  const handleOnSelectNextMonth = () =>
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1),
    );
  const handleOnSelectToday = () => setCurrentDate(new Date());

  const currentMonth = useMemo(() => {
    const month = currentDate?.toLocaleDateString(language, {
      month: 'long',
      year: 'numeric',
    });

    return month ? month[0].toUpperCase() + month.slice(1) : '';
  }, [currentDate, language]);

  const prevMonthDays = useMemo(
    () => getPrevMonthDays(currentDate).map((date) => date.getDate()),
    [currentDate],
  );
  const currentMonthDays = useMemo(
    () => getCurrentMonthDays(currentDate).map((date) => date.getDate()),
    [currentDate],
  );
  const nextMonthDays = useMemo(
    () => getNextMonthDays(currentDate).map((date) => date.getDate()),
    [currentDate],
  );

  const calendarItems = useCalendarItems({
    currentDate,
    activities,
    onShowMoreActivities,
  });

  return (
    <div
      className={`bg-surface border border-divider rounded-md flex flex-col ${className}`}
    >
      <div className="p-4 cluster-4 justify-between items-center flex-shrink-0">
        <div className="cluster-4 items-center">
          <Button
            data-testid="calendar-select-previous-month"
            type="tertiary"
            icon={
              languageDirection === 'rtl' ? (
                <ChevronRightIcon />
              ) : (
                <ChevronLeftIcon />
              )
            }
            onClick={handleOnSelectPrevMonth}
          />
          <span
            data-testid="calendar-current-month"
            className="text-xl font-semibold"
          >
            {currentMonth}
          </span>
          <Button
            data-testid="calendar-select-next-month"
            type="tertiary"
            icon={
              languageDirection === 'rtl' ? (
                <ChevronLeftIcon />
              ) : (
                <ChevronRightIcon />
              )
            }
            onClick={handleOnSelectNextMonth}
          />
        </div>
        <Button
          data-testid="calendar-select-current-month"
          type="ghost"
          size="medium"
          onClick={handleOnSelectToday}
        >
          {t('today')}
        </Button>
      </div>
      <div className="z-calendar flex-1 min-h-0 overflow-y-auto">
        {dayNames.map((name) => (
          <div
            key={name}
            className="z-calendar__day-name sticky top-0 bg-surface z-10"
          >
            {name}
          </div>
        ))}
        {prevMonthDays.map((day) => (
          <div
            key={day}
            className="z-calendar__day border-b p-2 text-xs text-on-disabled"
          >
            {day}
          </div>
        ))}
        {currentMonthDays.map((day) => (
          <CalendarDay
            day={day}
            currentDate={currentDate}
            clinicalEvents={clinicalEvents}
            onClinicalEventClick={onClinicalEventClick}
          />
        ))}
        {nextMonthDays.map((day) => (
          <div
            key={day}
            className="z-calendar__day border-b p-2 text-xs text-on-disabled"
          >
            {day}
          </div>
        ))}
        {calendarItems.map(
          ({
            activityId,
            name,
            state,
            isEmpty,
            isNonActionable,
            isDisabled,
            dateStartDay,
            dateEndDay,
            weekStart,
            onClick,
            showContinuationToPrevDate,
            showContinuationToNextDate,
            className: milestoneClassName,
            grid,
          }) => (
            <CalendarMilestone
              key={`${activityId}_${grid.rowStart}-${grid.columnStart}-${grid.columnEnd}`}
              name={name}
              state={state}
              isEmpty={isEmpty}
              isNonActionable={isNonActionable}
              isDisabled={isDisabled}
              onClick={onClick}
              dateStartDay={dateStartDay}
              dateEndDay={dateEndDay}
              weekStart={weekStart}
              showContinuationToPrevDate={showContinuationToPrevDate}
              showContinuationToNextDate={showContinuationToNextDate}
              className={milestoneClassName}
              grid={grid}
            />
          ),
        )}
      </div>
    </div>
  );
};

Calendar.propTypes = {
  className: PropTypes.string,
  activities: PropTypes.arrayOf(
    PropTypes.shape({
      dateStart: PropTypes.instanceOf(Date),
      dateEnd: PropTypes.instanceOf(Date),
      name: PropTypes.string,
      isEmpty: PropTypes.bool,
      isNonActionable: PropTypes.bool,
      onClick: PropTypes.func,
      state: PropTypes.oneOf([
        ACTIVITY_STATE__COMPLETED,
        ACTIVITY_STATE__ACTIVE,
        ACTIVITY_STATE__ABORTED,
        ACTIVITY_STATE__CANCELED,
        ACTIVITY_STATE__EXPIRED,
        ACTIVITY_STATE__SCHEDULED,
        ACTIVITY_STATE__PLANNED,
        ACTIVITY_STATE__OMITTED,
        ACTIVITY_STATE__SUSPENDED,
      ]),
    }),
  ),
  clinicalEvents: PropTypes.shape({
    appointments: PropTypes.array,
    medications: PropTypes.array,
    procedures: PropTypes.array,
  }),
  onShowMoreActivities: PropTypes.func,
  onClinicalEventClick: PropTypes.func,
};

Calendar.defaultProps = {
  className: '',
  activities: [],
  clinicalEvents: {},
  onShowMoreActivities: () => {},
  onClinicalEventClick: () => {},
};

export default Calendar;
